import React, { useState } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useNotifications } from '../../hooks/useNotifications.js';
import useToast from '../../hooks/useToast';

const NotificationsPage = () => {
  const { profileLoading } = useUserProfile();
  const {
    notifications,
    isLoading: loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    handleJoinAction,
    formatNotificationTime: formatTimeAgo,
    getNotificationTypeIcon: getNotificationIcon,
    getNotificationPriorityColor: getNotificationColor
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // all, unread, read
  const { showToast } = useToast();

  const handleActionButton = async (notification, actionButton) => {
    const { action } = actionButton;
    
    if (action === 'approve_join' || action === 'reject_join') {
      const type = action === 'approve_join' ? 'approve' : 'reject';
      const result = await handleJoinAction(notification, type);
      
      if (result.success) {
        showToast(
          `Join request ${type === 'approve' ? 'approved' : 'rejected'} successfully`,
          type === 'approve' ? 'success' : 'info'
        );
      } else {
        showToast(result.error || `Failed to ${type} join request`, 'error');
      }
    }
  };

  // For 'all'/'read' tabs, filter locally; for 'unread' we already queried unread_only
  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'read') return notif.read;
    return true;
  });

  const unreadCount = notifications.filter(notif => !notif.read).length;

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/role-based-dashboard' },
    { label: 'Notifications', path: '/notifications' }
  ];

  if (profileLoading || loading) {
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader />
        <div className='container mx-auto px-4 py-8'>
          <div className='flex flex-col items-center justify-center space-y-4 py-16'>
            <Icon name='Loader2' size={32} className='animate-spin text-primary' />
            <div className='text-lg text-muted-foreground'>Loading notifications...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-background'>
      <RoleBasedHeader />
      
      <div className='container mx-auto px-4 py-8'>
        <Breadcrumb items={breadcrumbItems} />
        
        {/* Header */}
        <div className='mb-8'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center'>
                <Icon name='Bell' size={24} className='text-white' />
              </div>
              <div>
                <h1 className='text-3xl font-bold text-foreground'>Notifications</h1>
                <p className='text-muted-foreground'>
                  {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
                </p>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <Button onClick={markAllAsRead} variant="outline">
                <Icon name="CheckCheck" size={16} className="mr-2" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className='mb-6'>
          <div className='flex space-x-1 bg-muted p-1 rounded-lg w-fit'>
            {[
              { key: 'all', label: 'All', count: notifications.length },
              { key: 'unread', label: 'Unread', count: unreadCount },
              { key: 'read', label: 'Read', count: notifications.length - unreadCount }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === tab.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {/* Notifications List */}
        <div className='space-y-4'>
          {filteredNotifications.length === 0 ? (
            <div className='text-center py-16'>
              <div className='w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4'>
                <Icon name='Bell' size={24} className='text-muted-foreground' />
              </div>
              <h3 className='text-lg font-medium text-foreground mb-2'>No notifications</h3>
              <p className='text-muted-foreground'>
                {filter === 'unread' ? "You're all caught up!" : 'No notifications to show.'}
              </p>
            </div>
          ) : (
            filteredNotifications.map(notification => (
              <div
                key={notification.id}
                className={`bg-card border rounded-lg p-4 transition-colors hover:bg-muted/50 ${
                  !notification.read ? 'border-l-4 border-l-primary bg-primary/5' : ''
                }`}
              >
                <div className='flex items-start gap-4'>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification.type, notification.priority)}`}>
                    <Icon name={getNotificationIcon(notification.type)} size={16} />
                  </div>
                  
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-start justify-between gap-4'>
                      <div className='flex-1'>
                        <h4 className='font-medium text-foreground mb-1'>{notification.title}</h4>
                        <p className='text-sm text-muted-foreground mb-2'>{notification.message}</p>
                        <div className='flex items-center gap-4 text-xs text-muted-foreground'>
                          <span>{formatTimeAgo(notification.created_at || notification.timestamp || notification.createdAt)}</span>
                          {notification.priority === 'high' && (
                            <span className='bg-red-100 text-red-700 px-2 py-1 rounded-full'>High Priority</span>
                          )}
                        </div>


                      </div>
                      
                      <div className='flex items-center gap-2'>
                        {!notification.read && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <Icon name="Check" size={14} />
                          </Button>
                        )}

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
